import React, { useRef, useState } from "react";
import { Formik, Field, Form, ErrorMessage, FormikHelpers } from "formik";
import { Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import PhonePicker from "@services/ui-components/src/PhonePicker/PhonePicker";
import { ReactSelect, Spinner, SpinnerLoader, MultiSelect } from "@services/ui-components";
import { ApolloError, useReactiveVar } from "@apollo/client";
import DropPhotosMultiply from "../../../DropPhotosMultiply/DropPhotosMultiply";
import {
    useCreateNewCompanyMutation,
    useGetCountriesForCompanyQuery,
    useGetCountryGeoForCompanyQuery,
    useGetCurrenciesQuery,
    useGetGmtQuery,
    useGetLanguagesForLanguageVersionsQuery,
    useGetLanguagesQuery,
} from "@me-team/host/main/company/graphql/company.hooks";
import LanguagesService from "../../../../services/LanguagesService/LanguagesService";
import { currentUserEmailVar } from "@me-team/host/main/globalVar/state";
import { refetchUserDataMenu } from "@me-team/host/src/apollo/apollo-client";
import { handlePaste } from "@me-team/host/src/utils/utils";
import { ValidationErrorExtensions } from "@me-team/host/src/utils/type";
import { OptionString } from "../../../Warehouse/WarehousesOperations/types";
import { Option } from "@me-team/common/src/interfaces/interfaces";
import { MultiOption } from "../../../../../../../services/ui-components/src/MultiSelect/MultiSelect";
import { getLanguageVersionOptions } from "../../../Me-menu/AllMenusPage/options";
import ScrollToErrorHelper from "../../../../services/ScrollToErrorHelper/ScrollToErrorHelper";
import { BusinessTypeSelector, WorkSchedulesSection } from "../../../index";
import {
    mapWeekValueToWorkSchedulesInput,
    WORK_SCHEDULE_TYPE_GENERAL,
    WORK_SCHEDULE_TYPE_KITCHEN,
} from "../../../Me-menu/WorkingHours/helpers/workingHoursHelpers";
import { WorkingHoursWeekValueDto } from "@me-pos/schedule-modal";
import { CompanyInput } from "@me-team/host/main/graphql/types";
import { BUSINESS_TYPE } from "../../../BusinessTypeSelector/businessType";
import { BusinessTypeSelectorControlProps } from "../../../CreateBranch/types";
import { foodBusinessFormValidationSchema } from "./constants";
import { useCountryCitiesPaginatedOptions } from "../../../../hooks/useCountryCitiesPaginatedOptions";
import CompanyCitySelectFields from "../../components/CompanyCitySelectFields";

interface InitialFormData {
    name: string;
    description: string;
    country: string;
    city: number | null;
    address: string;
    timezone: string;
    timezoneName: string;
    messageLanguage: string;
    languages: number[];
    primaryLanguage: number | null;
    phone: string;
    currency: string;
    email: string;
    website: string;
    image: string | null;
    customCity: string | null;
    generalWorkingHours: WorkingHoursWeekValueDto | null;
    kitchenWorkingHours: WorkingHoursWeekValueDto | null;
}

const CreateFoodBusinessCompanyForm: React.FC<BusinessTypeSelectorControlProps> = ({
    selectedBusinessType,
    businessTypeOptions,
    onBusinessTypeChange,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [countryId, setCountryId] = useState<number | null>(null);
    const nameFieldRef = useRef<HTMLInputElement>(null);
    const cityRef = useRef<HTMLDivElement>(null);
    const [photos, setPhotos] = useState([null]);
    const { data: timezoneData } = useGetGmtQuery();
    const { data: languagesData, loading: loadingLanguagesData } = useGetLanguagesQuery({
        variables: { isMessageOnly: true },
    });
    const { data: currenciesData, loading: currenciesDataLoading } = useGetCurrenciesQuery();
    const [createCompany] = useCreateNewCompanyMutation();
    const currentUserEmail = useReactiveVar(currentUserEmailVar);
    const [showCustomCityInput, setShowCustomCityInput] = useState<boolean>(false);
    const { data: countriesData } = useGetCountriesForCompanyQuery();
    const { data: countryGeoData, loading: loadingCountryGeo } = useGetCountryGeoForCompanyQuery({
        onCompleted(data) {
            const defaultCountry = data?.countryGeo?.country;
            if (defaultCountry?.id) {
                setCountryId(defaultCountry.id);
            }
        },
    });

    const {
        cityOptions,
        selectedCityOption,
        isCitiesInitialLoading,
        isCitiesSearchLoading,
        onCityMenuOpen,
        onCityMenuClose,
        onCitySearchChange,
        onCityMenuScrollToBottom,
        resetCities,
    } = useCountryCitiesPaginatedOptions({
        countryId,
        selectedCity: null,
        includeCustomCityOption: true,
        customCityOptionLabel: t("Other city"),
    });

    const countryOptions =
        countriesData?.relations.countries.map((country) => ({
            label: country.name,
            value: country.id.toString(),
        })) || [];

    const timezoneOptions =
        timezoneData?.relations.timezones.map((timezone) => ({
            label: timezone.name,
            value: timezone.id.toString(),
        })) || [];

    const currenciesOptions =
        currenciesData?.relations.currencies.map((currency) => ({
            label: `${currency.code} - ${currency.currency}`,
            value: currency.id.toString(),
        })) || [];

    const { data: languageVersionsData, loading: languageVersionsLoading } = useGetLanguagesForLanguageVersionsQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            console.error("error", error);
        },
    });

    const languageVersionOptions: Option[] = getLanguageVersionOptions(languageVersionsData?.relations?.languages);

    const countryGeo = countryGeoData?.countryGeo?.country;

    const initialValues: InitialFormData = {
        name: "",
        description: "",
        country: countryGeo?.id.toString() || "",
        city: null,
        address: "",
        timezone: countryGeo?.timezone.length ? countryGeo?.timezone[0].id.toString() : "",
        timezoneName: countryGeo?.timezone?.[0]?.name ?? "",
        messageLanguage: "",
        phone: countryGeo?.phoneCode?.code ? `+${countryGeo?.phoneCode?.code}` : "",
        currency: countryGeo?.currencies.length ? countryGeo?.currencies[0].id.toString() : "",
        email: currentUserEmail?.trim() || "",
        website: "",
        image: null,
        customCity: null,
        languages: [],
        primaryLanguage: null,
        generalWorkingHours: null,
        kitchenWorkingHours: null,
    };

    const handleSubmit = async (
        values: InitialFormData,
        { setSubmitting, setFieldError }: FormikHelpers<InitialFormData>
    ) => {
        const selectedCityId = values.city ? Number(values.city) : null;

        const input: CompanyInput = {
            category: BUSINESS_TYPE.FOOD,
            name: values.name,
            country: parseInt(values.country, 10),
            city: showCustomCityInput ? null : selectedCityId,
            otherCity: values?.customCity ? values?.customCity : null,
            address: values.address,
            phone: values.phone,
            email: values.email?.trim() || "",
            website: values.website,
            timezone: +values.timezone,
            currency: +values.currency,
            description: values.description,
            messageLanguage: +values.messageLanguage,
            languages: values.languages,
            primaryLanguage: values.primaryLanguage,
            images: photos,
            logo: values.image,
            deleted: false,
        };

        const validPhotos = photos.filter((photo) => photo != null);
        input.images = validPhotos;

        const generalWorkSchedules = mapWeekValueToWorkSchedulesInput(
            values.generalWorkingHours,
            WORK_SCHEDULE_TYPE_GENERAL
        );

        const kitchenWorkSchedules = mapWeekValueToWorkSchedulesInput(
            values.kitchenWorkingHours,
            WORK_SCHEDULE_TYPE_KITCHEN
        );

        input.workSchedules = [...generalWorkSchedules, ...kitchenWorkSchedules];

        try {
            await createCompany({
                variables: { input },
                onCompleted: async (data) => {
                    const branchId: string = String(data.createCompany.branches[0].id);
                    await refetchUserDataMenu();
                    branchId && navigate(`/create-owner/${branchId}`);
                },
            });
            setSubmitting(false);
        } catch (error) {
            if (error?.graphQLErrors?.[0]?.extensions?.category === "company.already_exists") {
                setFieldError("name", t("Company with the received name and phone number already exists!"));

                if (nameFieldRef.current) {
                    nameFieldRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                    nameFieldRef.current.focus();
                }
                return;
            }
            const validationErrors = (error.graphQLErrors[0].extensions as unknown as ValidationErrorExtensions)
                .validation;
            const websiteErrors = validationErrors.website;
            if (Array.isArray(websiteErrors)) {
                const longError = websiteErrors.find((e) =>
                    e.message.includes("This value is too long. It should have 2040 characters or less.")
                );
                if (longError) {
                    setFieldError("website", t("The value length for this field must not exceed 2040 characters."));
                }
            }
            console.error("Error creating branch:", error);
            setSubmitting(false);
        }
    };

    const isBaseLoading = loadingCountryGeo || loadingLanguagesData || currenciesDataLoading || languageVersionsLoading;

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={foodBusinessFormValidationSchema(t)}
            onSubmit={(values, actions) => handleSubmit(values, actions)}
            enableReinitialize
        >
            {({ setFieldValue, handleBlur, values, isSubmitting, errors, touched, submitCount }) => {
                const handleChangeCountry = async (selectedOption: OptionString) => {
                    const newCountryId = selectedOption?.value ? parseInt(selectedOption.value, 10) : null;

                    setFieldValue("city", null);
                    setFieldValue("customCity", null);
                    setShowCustomCityInput(false);
                    setFieldValue("country", selectedOption?.value || "");
                    setCountryId(newCountryId);

                    const selectedCountry = countriesData?.relations.countries.find(
                        (country) => country.id === newCountryId
                    );
                    if (selectedCountry?.timezone?.length) {
                        setFieldValue("timezone", selectedCountry.timezone[0].id.toString());
                        setFieldValue("timezoneName", selectedCountry.timezone[0].name);
                    } else {
                        setFieldValue("timezone", "");
                        setFieldValue("timezoneName", "");
                    }

                    resetCities();
                };

                const selectedLanguageIds = values.languages ?? [];

                const primaryLanguageOptions: Option[] = (languageVersionOptions ?? []).filter((option) =>
                    selectedLanguageIds.includes(option.value)
                );

                const primaryLanguageValue =
                    primaryLanguageOptions.find((opt) => opt.value === values.primaryLanguage) || null;

                return (
                    <Form>
                        <ScrollToErrorHelper
                            errors={errors}
                            submitClickedCount={submitCount}
                            refs={{ city: cityRef }}
                        />
                        <Row className="mt-6">
                            <Col lg={9} className="pe-lg-5">
                                <div className="mb-4">
                                    <BusinessTypeSelector
                                        title={t("Business type")}
                                        selectedBusinessType={selectedBusinessType}
                                        options={businessTypeOptions}
                                        onChange={onBusinessTypeChange}
                                        disabled={isBaseLoading}
                                    />
                                </div>
                                {isBaseLoading ? (
                                    <Spinner heightContent />
                                ) : (
                                    <>
                                        <h5 className="fs-4 text-capitalize mb-4">{t("Basic information")}</h5>
                                        <div>
                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="registration-name-field"
                                                            className="fs-7 fw-bold"
                                                        >
                                                            <span className="required-label">{t("Company name")}</span>
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} xxl={10}>
                                                        <div className="position-relative">
                                                            <Field
                                                                id="registration-name-field"
                                                                name="name"
                                                                innerRef={nameFieldRef}
                                                                placeholder={t("Enter a company name")}
                                                                className={`form-control rounded-1 lh-22 pe-5 text-truncate ${errors.name && touched.name ? "border-danger" : "border-secondary"}`}
                                                                maxLength={50}
                                                            />
                                                            <div className="pt-1 position-absolute top-50 translate-middle-y end-0 me-3">
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {t("This data is displayed in the qr-menu")}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i className="bi bi-info-circle"></i>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Col sm={9} xxl={10} className="ms-auto">
                                                    <ErrorMessage name="name">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                </Col>
                                            </div>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-country-field"
                                                        className="required-label fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("country")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <ReactSelect
                                                        id="registration-country-field"
                                                        name="country"
                                                        inputAutoComplete="new-password"
                                                        options={countryOptions}
                                                        value={
                                                            countryOptions.find(
                                                                (option) =>
                                                                    String(option.value) === String(values.country)
                                                            ) ?? null
                                                        }
                                                        onChange={handleChangeCountry}
                                                        placeholder={t("Select country")}
                                                        isSearchable
                                                    />
                                                    <ErrorMessage name="country">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <CompanyCitySelectFields
                                                cityFieldId="registration-city-field"
                                                customCityFieldId="registration-customCity-field"
                                                cityRef={cityRef}
                                                cityOptions={cityOptions}
                                                selectedCityOption={selectedCityOption}
                                                showCustomCityInput={showCustomCityInput}
                                                setShowCustomCityInput={setShowCustomCityInput}
                                                isLoading={isCitiesInitialLoading || isCitiesSearchLoading}
                                                disabled={values.country === ""}
                                                onMenuOpen={onCityMenuOpen}
                                                onMenuClose={onCityMenuClose}
                                                onInputChange={onCitySearchChange}
                                                onMenuScrollToBottom={onCityMenuScrollToBottom}
                                            />

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-address-field"
                                                        className="required-label fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("address")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <div className="position-relative">
                                                        <Field
                                                            id="registration-address-field"
                                                            name="address"
                                                            autoComplete="address-line1"
                                                            placeholder={t("Enter your location")}
                                                            className={`form-control rounded-1 lh-22 pe-5 ${errors.address && touched.address ? "border-danger" : "border-secondary"}`}
                                                            maxLength={200}
                                                        />
                                                        <div className="pt-1 position-absolute top-50 translate-middle-y end-0 me-3">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t("This data is displayed in the qr-menu")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle"></i>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="address">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-phone-field"
                                                        className="required-label fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("phone")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <PhonePicker
                                                        id="registration-phone-field"
                                                        name="phone"
                                                        value={values.phone}
                                                    />
                                                    <ErrorMessage name="phone">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-email-field"
                                                        className="fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("email")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <Field
                                                        id="registration-email-field"
                                                        name="email"
                                                        placeholder={t("Add E-mail")}
                                                        className="form-control rounded-1 lh-22"
                                                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                            const trimmedValue = e.target.value.trim();
                                                            e.target.value = trimmedValue;
                                                            setFieldValue("email", trimmedValue, false);
                                                            handleBlur(e);
                                                        }}
                                                    />
                                                    <ErrorMessage name="email">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-website-field"
                                                        className="fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("website")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <Field
                                                        id="registration-website-field"
                                                        name="website"
                                                        placeholder={t("Site address")}
                                                        className={`form-control rounded-1 lh-22 pe-5 ${errors.website && touched.website ? "border-danger" : "border-secondary"}`}
                                                        maxLength={2040}
                                                        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                                                            handlePaste(e, 2040)
                                                        }
                                                    />
                                                    <ErrorMessage name="website">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="registration-timezone-field"
                                                            className="required-label fs-7 fw-bold"
                                                        >
                                                            {t("timezone")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} xxl={10}>
                                                        <ReactSelect
                                                            id="registration-timezone-field"
                                                            name="timezone"
                                                            options={timezoneOptions}
                                                            value={timezoneOptions.find(
                                                                (option: any) => option.value === values.timezone
                                                            )}
                                                            onChange={(selectedOption: any) => {
                                                                setFieldValue(
                                                                    "timezone",
                                                                    selectedOption ? selectedOption.value : ""
                                                                );
                                                                setFieldValue(
                                                                    "timezoneName",
                                                                    selectedOption ? selectedOption.label : ""
                                                                );
                                                            }}
                                                            placeholder={t("Select Gmt")}
                                                            isSearchable
                                                        />
                                                    </Col>
                                                </Row>
                                                <Col sm={9} xxl={10} className="ms-auto">
                                                    <ErrorMessage name="timezone">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                </Col>
                                            </div>

                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="registration-messageLanguage-field"
                                                            className="required-label fs-7 fw-bold"
                                                        >
                                                            {t("Message language")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} xxl={10}>
                                                        <ReactSelect
                                                            id="registration-messageLanguage-field"
                                                            name="messageLanguage"
                                                            options={LanguagesService.languageFilter(
                                                                languagesData.relations.languages
                                                            )}
                                                            value={LanguagesService.languageFilter(
                                                                languagesData.relations.languages
                                                            ).find(
                                                                (option: any) => option.value === values.messageLanguage
                                                            )}
                                                            onChange={(selectedOption: any) => {
                                                                setFieldValue(
                                                                    "messageLanguage",
                                                                    selectedOption ? selectedOption.value : ""
                                                                );
                                                            }}
                                                            placeholder={t("Select Language")}
                                                            isSearchable
                                                        />
                                                    </Col>
                                                </Row>
                                                <Col sm={9} xxl={10} className="ms-auto">
                                                    <ErrorMessage name="messageLanguage">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                </Col>
                                            </div>
                                            <Row className="mb-4">
                                                <Col sm={3} xxl={2} className="text-start mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-languages-field"
                                                        className="required-label"
                                                    >
                                                        <span className="fs-7 fw-bold text-start">
                                                            {t("Language versions of the menu")}
                                                        </span>
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <MultiSelect
                                                        id="registration-languages-field"
                                                        name="languages"
                                                        placeholder={t("Select additional menu languages")}
                                                        options={languageVersionOptions ?? []}
                                                        value={languageVersionOptions.filter((option) =>
                                                            selectedLanguageIds.includes(option.value)
                                                        )}
                                                        onChange={(selectedOptions: MultiOption[] | null) => {
                                                            const newIds = (selectedOptions ?? []).map(
                                                                (opt) => opt.value
                                                            );
                                                            setFieldValue("languages", newIds);
                                                            if (
                                                                values.primaryLanguage != null &&
                                                                !newIds.includes(values.primaryLanguage)
                                                            ) {
                                                                setFieldValue("primaryLanguage", null);
                                                            }
                                                        }}
                                                        isSearchable
                                                    />
                                                    <ErrorMessage name="languages">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>
                                            <Row className="mb-4">
                                                <Col sm={3} xxl={2} className="text-start mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-primaryLanguage-field"
                                                        className="required-label"
                                                    >
                                                        <span className="fs-7 fw-bold text-start">
                                                            {t("Main language menu")}
                                                        </span>
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <ReactSelect
                                                        id="registration-primaryLanguage-field"
                                                        name="primaryLanguage"
                                                        value={primaryLanguageValue}
                                                        options={primaryLanguageOptions}
                                                        placeholder={t("Choose a language")}
                                                        onChange={(option: Option | null) =>
                                                            setFieldValue(
                                                                "primaryLanguage",
                                                                option ? option.value : null
                                                            )
                                                        }
                                                        isSearchable={true}
                                                        disabled={!primaryLanguageOptions?.length}
                                                    />
                                                    <ErrorMessage name="primaryLanguage">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-currency-field"
                                                        className="required-label fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("Currency")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <ReactSelect
                                                        id="registration-currency-field"
                                                        name="currency"
                                                        options={currenciesOptions}
                                                        value={currenciesOptions.find(
                                                            (option: any) => option.value === values.currency
                                                        )}
                                                        onChange={(selectedOption: any) => {
                                                            setFieldValue(
                                                                "currency",
                                                                selectedOption ? selectedOption.value : ""
                                                            );
                                                        }}
                                                        placeholder={t("Select Currency")}
                                                        isSearchable
                                                    />
                                                    <ErrorMessage name="currency">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-description-field"
                                                        className="fs-7 fw-bold pt-2"
                                                    >
                                                        {t("description")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <div className="position-relative">
                                                        <Field
                                                            as="textarea"
                                                            id="registration-description-field"
                                                            name="description"
                                                            placeholder={t("description")}
                                                            maxLength={200}
                                                            className="form-control pe-5 rounded-1"
                                                        />
                                                        <div className="pt-1 position-absolute top-0 end-0 mt-1 me-3">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t("This data is displayed in the qr-menu")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle"></i>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="description">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                    <div className="fs-7 text-grey text-start mt-1 px-1">
                                                        {`${values.description.length}/200`}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4 pt-6">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label htmlFor="registration-photo-field" className="fs-7 fw-bold">
                                                        {t("Photo of the branch")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <div className="mb-2">
                                                        <p className="fs-7 mb-0">
                                                            {t(
                                                                "Add jpeg images of the branch premises, interior, etc."
                                                            )}
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t("This data is displayed in the qr-menu")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle ms-1 fs-7 pt-1"></i>
                                                            </OverlayTrigger>
                                                        </p>
                                                    </div>
                                                    <Row className="gx-0">
                                                        <DropPhotosMultiply
                                                            photosArray={photos}
                                                            setPhotos={setPhotos}
                                                        />
                                                    </Row>
                                                    <div>
                                                        <p className="fs-7 text-grey mb-6">{t("max-size")}</p>
                                                    </div>
                                                </Col>
                                            </Row>
                                            {!!values.timezoneName && (
                                                <WorkSchedulesSection
                                                    title="WorkingHours.Work Schedule"
                                                    timeZone={values.timezoneName}
                                                    values={values}
                                                    errors={errors}
                                                    touched={touched}
                                                    setFieldValue={setFieldValue}
                                                    fieldNames={{
                                                        general: "generalWorkingHours",
                                                        kitchen: "kitchenWorkingHours",
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                            </Col>
                            <Col lg={3} className="ms-auto ps-lg-0">
                                <h5 className="fs-4 mb-4">{t("logo")}</h5>
                                <ImageCrop setFieldValue={setFieldValue} modalTitle={t("Company logo")} />
                            </Col>
                        </Row>
                        <div className="my-4 mt-lg-0">
                            <button
                                type="submit"
                                className="btn btn-primary fw-bold custom-btn rounded-1"
                                disabled={isSubmitting || isBaseLoading}
                            >
                                {isSubmitting ? (
                                    <SpinnerLoader variant="light" loadingText={t("Creating...")} />
                                ) : (
                                    t("Create")
                                )}
                            </button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default React.memo(CreateFoodBusinessCompanyForm);
